/**
 * Utility functions for computing session state and redirect state.
 * Extracted from ProtectedRoute for better testability.
 */

import type { Location } from 'react-router-dom'
import type { AuthStatus } from '../../../contexts/AuthContext'
import type { User } from '../../../contexts/AuthContext'

/**
 * Computes whether the session has expired based on auth state.
 * @param hydrated Whether auth has been hydrated
 * @param status Current auth status
 * @param user Current user (null if not authenticated)
 * @returns true if session has expired, false otherwise
 */
export function computeSessionExpired(
    hydrated: boolean,
    status: AuthStatus,
    user: User | null
): boolean {
    return hydrated && status === 'signedOut' && user === null
}

/**
 * Gets the redirect state object for navigation to login.
 * @param isIntentionalLogout Whether the logout was intentional
 * @param sessionExpired Whether the session has expired
 * @param location Current location (for 'from' field)
 * @returns State object for Navigate component
 */
export function getRedirectState(
    isIntentionalLogout: boolean,
    sessionExpired: boolean,
    location: Location
): { from: Location; loggedOut?: boolean; sessionExpired?: boolean } {
    return {
        from: location,
        ...(isIntentionalLogout ? { loggedOut: true } : { sessionExpired: sessionExpired }),
    }
}
